<template>
    <div id="edit-organizations" v-if="payload.id">
      <label label @click="$router.go(-1)" class="back">
        <figure>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9.57 5.93018L3.5 12.0002L9.57 18.0702M20.5 12.0002H3.67" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </figure>
        <p>Voltar</p>
      </label>
      <section>
        <h1><strong>Crie um {{ isChamber ? 'câmara' : 'setor' }}</strong> (obrigatório)</h1>
        <input type="text" placeholder="Ex: Secretaria de Segurança e Trânsito" name="name" id="namet" v-model="payload.name">
      </section>
      <section>
        <h1><strong>Iniciais do {{ isChamber ? 'câmara' : 'setor' }}</strong> (obrigatório)</h1>
        <input type="text" placeholder="Ex: S.M.S.T" name="acronym" id="acronym" v-model="payload.acronym">
      </section>
      <section class="spacing">
        <h1><strong>Status</strong> (obrigatório)</h1>
        <label for="stats">
          <input type="checkbox" name="stats" id="stats" v-model="payload.status">
          <span></span>
        </label>
      </section>
      <nav>
        <input v-if="!isUpdatingOrganization" type="button" value="Salvar" @click="updateOrganization" :disabled="!payload.name || !payload.acronym">
        <SpinnerLoaderButton v-else :height="60" :maxWidth="464"/>
        <input type="button" value="Cancelar" @click="$router.push({ name: 'admin.home' })">
      </nav>
    </div>
    <SpinnerLoader v-else />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAxios } from '@/api/axios'
import { OrganizationModel } from '@/models/OrganizationModel'
import { toast } from 'vue3-toastify'
import { AxiosResponse } from 'axios'
import OrganizationStatusEnum from '@/enums/OrganizationStatusEnum'
import SpinnerLoaderButton from '@/components/loader/SpinnerLoaderButton.vue'
import SpinnerLoader from '@/components/loader/SpinnerLoader.vue'
import { user } from '@/global/states/GlobalState'
import { TenantSectorEnum } from '@/enums/Tenants/TenantSectorEnum'

const props = defineProps<{ id: number }>()
const axios = useAxios()
const isChamber = computed(() => user.value.tenant?.sector === TenantSectorEnum.CHAMBER)
const payload = ref<Partial<OrganizationModel>>({})
const isUpdatingOrganization = ref(false)

function updateOrganization () {
  isUpdatingOrganization.value = true

  axios.put('/api/organizations', { ...payload.value, status: payload.value.status ? OrganizationStatusEnum.ACTIVE : OrganizationStatusEnum.INACTIVE, organization_ids: [payload.value.id] })
    .then(() => {
      toast.success(isChamber.value ? 'Câmara editada com sucesso' : 'Setor editado com sucesso')
    })
    .finally(() => isUpdatingOrganization.value = false)
}

function getOrganization () {
  axios.get(`/api/organizations/${props.id}`)
    .then((res: AxiosResponse<OrganizationModel>) => {
      payload.value = { ...res.data, status: res.data.status == OrganizationStatusEnum.ACTIVE }
    })
}

onMounted(() => {
  getOrganization()
})
</script>

<style lang="scss">
  #edit-organizations{
    width: 100%;
    max-width: 1200px;
    display: flex;
    flex-direction: column;
    align-items: start;
    row-gap: 28px;
    padding: 32px 0 0 0;
    section{
      width: 100%;
      display: flex;
      flex-direction: column;
      h1{
        color: #090909;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        margin: 0 0 13px 0;
        text-align: start;
        strong{
          color: #090909;
          font-family: 'Inter', sans-serif;
          font-size: 20px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
        }
      }
      h2{
        color: #6F6F6F;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        text-align: start;
        margin: -13px 0 13px 0;
      }
      input[type="text"]{
        width: 100%;
        height: 55px;
        border-radius: 14px;
        border: 1px solid #D3D3D3;
        color: $fluxoo-primary-color;
        font-family: 'Inter', sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        padding: 15px 20px;
        box-sizing: border-box;
        transition: border-color .2s, color .2s, background .2s;
        &:focus{
          outline: none;
        }
        &::placeholder{
          color: #090909;
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
          transition: color .2s;
        }
      }
      label{
        width: 105px;
        display: flex;
        align-items: center;
        column-gap: 10px;
        cursor: pointer;
        input{
          display: none;
          &:checked{
            & ~ span{
              background: $fluxoo-primary-color-hover;
              &::before{
                background: $fluxoo-primary-color;
                left: calc(100% - 25px);
              }
              &::after{
                content: 'Ativo';
                color: $fluxoo-primary-color;
              }
            }
          }
        }
        span{
          width: 43px;
          height: 11px;
          background: #949494;
          border-radius: 12px;
          position: relative;
          transition: background .2s;
          &::before{
            content: '';
            width: 25px;
            height: 25px;
            background: #D5D5D5;
            border-radius: 50%;
            position: absolute;
            top: -7px;
            left: 0;
            transition: background .2s, left .2s;
          }
          &::after{
            content: 'Inativo';
            position: absolute;
            left: calc(100% + 10px);
            bottom: -4px;
            color: #949494;
            font-family: 'Inter', sans-serif;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
            transition: color .2s;
          }
        }
      }
    }
    nav{
      width: 100%;
      max-width: 1200px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 15px 32px;
      border-radius: 16px;
      background: #FFF;
      box-shadow: 0px 0px 25px 0px rgba(0, 0, 0, 0.12);
      box-sizing: border-box;
      position: sticky;
      bottom: 5px;
      column-gap: 20px;
      margin: auto 0 0 0;
      input{
        border: none;
        cursor: pointer;
        &:first-child{
          width: 100%;
          max-width: 464px;
          height: 60px;
          border-radius: 30px;
          background: $fluxoo-primary-color;
          color: #FFF;
          text-align: center;
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          font-style: normal;
          font-weight: 600;
          line-height: normal;
          transition: background .2s;
          &:hover{
            background: $fluxoo-primary-color-hover;
          }
        }
        &:last-child{
          color: #737373;
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          font-style: normal;
          font-weight: 600;
          line-height: normal;
          background: unset;
          transition: color .2s;
          &:hover{
            color: $fluxoo-primary-color;
          }
        }
      }
    }
  }

  @media(max-width: 425px){
    #create-organizations{
      padding: 25px 0 120px 0;
      nav{
        animation: enterToBottom ease 0.9s;
        width: 100vw;
        position: fixed;
        left: 0;
        bottom: 0;
        border-radius: 20px 20px 0px 0px;
        background: #FFF;
        box-shadow: 0px 0px 25px 0px rgba(0, 0, 0, 0.12);
        padding: 15px;
        box-sizing: border-box;
        z-index: 4;
      }
    }
  }
</style>
